import { useMemo, useRef, useCallback } from 'react';
import useSWR from 'swr';
import { useApiClient } from '../api/client';
import { EXCHANGES } from '../constants/exchanges';

/**
 * Dashboard data: positions, hedging, funding rates, aggregates, system metrics.
 * Dependencies: currentTab, isPaused, selectedExchanges, auth, liveFundingRates, isConnected.
 */
export function useDashboardData({
  currentTab,
  isPaused,
  selectedExchanges,
  auth,
  liveFundingRates = {},
  isConnected = false,
}) {
  const { apiGet } = useApiClient();
  const selectedAccountId = auth?.selectedAccountId;
  const cacheRef = useRef({
    positions: null,
    hedging: null,
    fundingRates: null,
    timestamps: { positions: null, hedging: null, fundingRates: null },
  });

  const fetcher = useCallback(
    async (url) => {
      try {
        return await apiGet(url, { timeout: 45000 });
      } catch (err) {
        console.error('API Error for', url, ':', err.message);
        throw err;
      }
    },
    [apiGet]
  );

  const cachedFetcher = useCallback(async (url, cacheKey) => {
    try {
      const data = await fetcher(url);
      cacheRef.current[cacheKey] = data;
      cacheRef.current.timestamps[cacheKey] = Date.now();
      return data;
    } catch (error) {
      const cached = cacheRef.current[cacheKey];
      if (cached) {
        console.log(`Error fetching ${url}, using cached data`);
        return cached;
      }
      throw error;
    }
  }, [fetcher]);

  const positionsApiUrl = useMemo(() => {
    if (currentTab === 'admin' || isPaused) return null;
    const selected = Object.entries(selectedExchanges)
      .filter(([, sel]) => sel)
      .map(([ex]) => ex);
    const param = selected.join(',');
    return `/api/positions${param ? `?exchanges=${param}` : ''}`;
  }, [currentTab, isPaused, selectedExchanges]);

  const hedgingApiUrl = useMemo(() => {
    if (currentTab === 'admin' || currentTab === 'history' || isPaused) return null;
    const selected = Object.entries(selectedExchanges)
      .filter(([, sel]) => sel)
      .map(([ex]) => ex);
    const param = selected.join(',');
    return `/api/hedging${param ? `?exchanges=${param}` : ''}`;
  }, [currentTab, isPaused, selectedExchanges]);

  const fundingRatesKey =
    currentTab === 'admin' || currentTab === 'history' || isPaused || isConnected
      ? null
      : ['/api/funding-rates', selectedAccountId];

  const fundingAggregatesKey =
    currentTab === 'dashboard' && auth
      ? ['/api/history/funding-aggregates', 'all', selectedAccountId]
      : null;

  const { data: positionsData, error: positionsError, isLoading, mutate: mutatePositions } = useSWR(
    positionsApiUrl ? [positionsApiUrl, selectedAccountId] : null,
    ([url]) => cachedFetcher(url, 'positions'),
    {
      refreshInterval: isPaused ? 0 : currentTab === 'history' ? 30000 : 5000,
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 2000,
      onError: (e) => console.error('Positions API error:', e),
    }
  );

  const { data: hedgingData, error: hedgingError, mutate: mutateHedging } = useSWR(
    hedgingApiUrl ? [hedgingApiUrl, selectedAccountId] : null,
    ([url]) => cachedFetcher(url, 'hedging'),
    {
      refreshInterval: isPaused ? 0 : 5000,
      onError: (e) => console.error('Hedging API error:', e),
    }
  );

  const { data: fundingRatesData, mutate: mutateFundingRates } = useSWR(
    fundingRatesKey,
    fundingRatesKey ? ([url]) => cachedFetcher(url, 'fundingRates') : null,
    {
      refreshInterval: isPaused ? 0 : 5000,
      onError: (e) => console.error('Funding rates API error:', e),
    }
  );

  const { data: systemMetrics } = useSWR(
    isPaused ? null : '/api/system/metrics',
    (url) => cachedFetcher(url, 'systemMetrics'),
    {
      refreshInterval: isPaused ? 0 : 5000,
      revalidateOnFocus: false,
      dedupingInterval: 2000,
    }
  );

  const { data: fundingAggregates } = useSWR(
    fundingAggregatesKey,
    fundingAggregatesKey ? ([url]) => apiGet(`${url}?range=all`, { timeout: 15000 }) : null,
    {
      refreshInterval: currentTab === 'dashboard' && !isPaused ? 30000 : 0,
      revalidateOnFocus: false,
      dedupingInterval: 5000,
    }
  );

  const fundingNetByMarket = fundingAggregates?.by_market || [];

  const effectivePositionsData =
    isPaused && cacheRef.current.positions
      ? cacheRef.current.positions
      : positionsData;
  const effectiveHedgingData =
    isPaused && cacheRef.current.hedging ? cacheRef.current.hedging : hedgingData;
  const effectiveFundingRatesData =
    isPaused && cacheRef.current.fundingRates
      ? cacheRef.current.fundingRates
      : fundingRatesData;

  const filteredPositionsData = useMemo(() => {
    if (!effectivePositionsData) return null;
    const filtered = { ...effectivePositionsData };
    EXCHANGES.forEach((ex) => {
      const data = effectivePositionsData[ex];
      filtered[ex] = data
        ? {
            ...data,
            positions: selectedExchanges[ex] ? [...(data.positions || [])] : [],
          }
        : { exchange: ex, positions: [], total_balance: 0, total_unrealized_pnl: 0 };
    });
    filtered.hedging_analysis = effectivePositionsData.hedging_analysis || [];
    filtered.last_updated = effectivePositionsData.last_updated;
    return filtered;
  }, [effectivePositionsData, selectedExchanges]);

  const enrichedPositionsData = useMemo(() => {
    if (!filteredPositionsData) return null;
    const enrichPositions = (positions) => {
      if (!positions || !Array.isArray(positions)) return [];
      return positions.map((position) => {
        const rateKey = `${position.exchange}_${position.pair}`;
        const wsRate = liveFundingRates[rateKey];
        if (wsRate) {
          return {
            ...position,
            funding_rate_info: {
              ...position.funding_rate_info,
              current_rate: wsRate.current_rate,
              next_funding_time: wsRate.next_funding_time,
              countdown_seconds: wsRate.countdown_seconds,
              last_updated: wsRate.last_updated,
            },
          };
        }
        return position;
      });
    };
    const enriched = { ...filteredPositionsData };
    EXCHANGES.forEach((ex) => {
      const data = filteredPositionsData[ex];
      enriched[ex] = data
        ? { ...data, positions: enrichPositions(data.positions) }
        : data;
    });
    return enriched;
  }, [filteredPositionsData, liveFundingRates]);

  const hedgingAnalysis = useMemo(() => {
    if (!effectiveHedgingData?.hedging_analysis) return [];
    return effectiveHedgingData.hedging_analysis
      .map((analysis) => {
        const filtered = { ...analysis };
        EXCHANGES.forEach((ex) => {
          if (!selectedExchanges[ex]) filtered[`${ex}_position`] = null;
        });
        const positions = EXCHANGES.map((ex) => filtered[`${ex}_position`]).filter(Boolean);
        if (positions.length === 0) return null;
        if (positions.length === 1) {
          filtered.status = 'unhedged';
          filtered.hedge_ratio = 0.0;
          const pos = positions[0];
          const positionValue = pos.notional || (pos.size * pos.mark_price);
          filtered.net_exposure = pos.side === 'LONG' ? positionValue : -positionValue;
          filtered.aggregated_pnl = pos.unrealized_pnl || 0;
        } else {
          const sides = positions.map((p) => p.side);
          const hasLong = sides.includes('LONG');
          const hasShort = sides.includes('SHORT');
          const areOpposite = hasLong && hasShort;
          const exposures = positions.map((pos) => {
            const v = pos.notional || (pos.size * pos.mark_price);
            return pos.side === 'LONG' ? v : -v;
          });
          const netExposure = exposures.reduce((s, e) => s + e, 0);
          const totalExposure = exposures.reduce((s, e) => s + Math.abs(e), 0);
          const hedgeRatio =
            totalExposure > 0 ? 1 - Math.abs(netExposure) / totalExposure : 0.0;
          const minPositionValue = Math.min(
            ...positions.map((pos) => pos.notional || (pos.size * pos.mark_price))
          );
          if (areOpposite && Math.abs(netExposure) < minPositionValue * 0.1) {
            filtered.status = 'fully_hedged';
          } else if (areOpposite) {
            filtered.status = 'partially_hedged';
          } else {
            filtered.status = 'unhedged';
          }
          filtered.net_exposure = netExposure;
          filtered.hedge_ratio = hedgeRatio;
          filtered.aggregated_pnl = positions.reduce(
            (sum, pos) => sum + (pos.unrealized_pnl || 0),
            0
          );
        }
        return filtered;
      })
      .filter(Boolean);
  }, [effectiveHedgingData?.hedging_analysis, selectedExchanges]);

  const error = positionsError || hedgingError;

  const manualRefresh = useCallback(() => {
    cacheRef.current = {
      positions: null,
      hedging: null,
      fundingRates: null,
      timestamps: { positions: null, hedging: null, fundingRates: null },
    };
    mutatePositions();
    mutateHedging();
    mutateFundingRates();
  }, [mutatePositions, mutateHedging, mutateFundingRates]);

  return {
    positionsData: enrichedPositionsData,
    hedgingAnalysis,
    fundingNetByMarket,
    fundingRatesData: effectiveFundingRatesData,
    systemMetrics,
    isLoading,
    error,
    mutate: { positions: mutatePositions, hedging: mutateHedging, fundingRates: mutateFundingRates },
    manualRefresh,
  };
}
